import {Button, DatePicker, Form, Select, Tag} from 'antd';
import {useForm} from 'antd/es/form/Form';
import {SearchOutlined, MoreOutlined} from '@ant-design/icons';
import Table, {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../common/hooks';
import {MAIN_FILTER, SWAP_DATA} from './swap.types';
import {pageNumberChange, pageSizeChange, sortBy, swapDetail, swapList} from './swapSlice';
import {formatDate} from '../../utils/formatDate';
import {authStationsList} from '../setup/station/stations/stationsSlice';
import {racksList} from '../setup/rack/racks/racksSlice';
import {debounce} from 'lodash';
import moment from 'moment';
import SwapDetail from './SwapDetailModal';
import {customersList} from '../customers/customersSlice';
import {debounceDelay, sortbyFilter} from '../../common/constants';

const Swap = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.swap);
  const [searchForm] = useForm();
  const [isRackDisable, setIsRackDisable] = useState(true);
  const stationOptions = useAppSelector((state) => state.stations.stations.pageData);
  const rackOptions = useAppSelector((state) => state.racks.racks.pageData);
  const [swapDetailModalOpen, setSwapDetailModalOpen] = useState(false);
  const customerOptions = useAppSelector((state) => state.customers.customers.pageData);
  const [mainFilter, setMainFilter] = useState<MAIN_FILTER>();

  useEffect(() => {
    dispatch(
      swapList({
        body: mainFilter,
        filters: {
          page_number: state.swapList.page_number,
          page_size: state.swapList.page_size,
          search: state.swapList.search,
          sort_by: state.swapList.sort_by,
          ascending: state.swapList.ascending,
        },
      }),
    )
      .unwrap()
      .then(() => {
        dispatch(authStationsList({is_active: true}));
        dispatch(customersList({is_active: true}));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.swapList.page_number,
    state.swapList.page_size,
    state.swapList.search,
    state.swapList.sort_by,
    state.swapList.ascending,
  ]);

  const columns: ColumnsType<SWAP_DATA> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return <>{(state.swapList.page_number - 1) * state.swapList.page_size + index + 1}</>;
      },
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
    },
    {
      title: 'Cabinet Name',
      dataIndex: 'rack_name',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      render: (x) => {
        return <>{formatDate(x)}</>;
      },
    },

    {
      title: ' Status',
      dataIndex: 'status',
      render: (x) => {
        return <>{x.toUpperCase()}</>;
      },
    },
    {
      title: 'Is Terminated',
      dataIndex: 'is_failed',
      render: (x) => {
        return <>{x ? <Tag color='red'> True</Tag> : <Tag color='green'> False</Tag>}</>;
      },
    },

    {
      title: 'Amount',
      dataIndex: 'amount',
    },

    {
      title: 'More',
      render: (_, record) => {
        return (
          <MoreOutlined
            style={{scale: '2', cursor: 'pointer', marginRight: '20px'}}
            rotate={90}
            onClick={() => {
              dispatch(swapDetail(record.id))
                .unwrap()
                .then(() => {
                  setSwapDetailModalOpen(true);
                });
            }}
          />
        );
      },
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormSubmit = (values: {
    rack_id?: number[];
    station_id?: number[];
    date?: any;
    customer_id: number[];
    status: string;
  }) => {
    const payload: MAIN_FILTER = {};
    if (values.station_id !== undefined && values.station_id?.length !== 0) {
      payload.station_id = values.station_id;
    }
    if (values.rack_id !== undefined && values.station_id?.length !== 0) {
      payload.rack_id = values.rack_id;
    }
    if (values.customer_id !== undefined && values.customer_id?.length !== 0) {
      payload.customer_id = values.customer_id;
    }

    payload.status = values.status;

    if (values.date !== undefined && values.date !== null) {
      payload.from_date = `${values.date[0].format('YYYY/MM/DD')}`;
      payload.to_date = `${values.date[1].format('YYYY/MM/DD')}`;
    }
    setMainFilter(payload);
    dispatch(
      swapList({
        body: payload,
        filters: {
          page_number: state.swapList.page_number,
          page_size: state.swapList.page_size,
          search: state.swapList.search,
          sort_by: state.swapList.sort_by,
        },
      }),
    );
  };

  const handleStationBouncing = debounce((e) => {
    dispatch(authStationsList({search: e, is_active: true}));
  }, debounceDelay);

  const handleCustomerBouncing = debounce((e) => {
    dispatch(customersList({search: e, is_active: true}));
  }, debounceDelay);

  // const showData: SWAP_DATA[] = state.swapList.pageData.filter((x) => {
  //   if (x.swappingStatus !== false) return x;
  // });
  return (
    <div>
      <Form layout='inline' name='searchForm' form={searchForm} onFinish={handleFormSubmit}>
        <Form.Item name='station_id'>
          <Select
            style={{width: '200px'}}
            placeholder='Select station'
            showArrow
            allowClear
            filterOption={false}
            mode='multiple'
            maxTagCount={1}
            onSearch={(e) => {
              if (e.length > 2) handleStationBouncing(e);
            }}
            onChange={(e) => {
              searchForm.setFieldsValue({rack_id: undefined});

              if (e.length < 1) {
                searchForm.setFieldsValue({rack_id: undefined});
                setIsRackDisable(true);
              } else {
                dispatch(racksList({station_id: e}));
                setIsRackDisable(false);
              }
            }}
          >
            {stationOptions.map((x, i) => (
              <Select.Option key={i} value={x.id}>
                {x.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name='rack_id'>
          <Select
            placeholder='Select Cabinet'
            style={{width: '200px'}}
            mode='multiple'
            maxTagCount={1}
            allowClear
            showArrow
            disabled={isRackDisable}
          >
            {rackOptions.map((x, i) => (
              <Select.Option key={i} value={x.id}>
                {x.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name='customer_id'>
          <Select
            style={{width: '200px'}}
            placeholder='Select customer'
            showArrow
            allowClear
            mode='multiple'
            maxTagCount={1}
            filterOption={false}
            onSearch={(e) => {
              if (e.length > 2) handleCustomerBouncing(e);
            }}
          >
            {customerOptions.map((x, i) => (
              <Select.Option key={i} value={x.id}>
                {x.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {/* <Form.Item name='status' initialValue={'all'}>
          <Select
            style={{width: '200px'}}
            options={[
              {
                value: 'all',
                label: 'All',
              },
              {
                value: 'initiated',
                label: 'Initiated',
              },
              {
                value: 'inprogress',
                label: 'In Progress',
              },

              {
                value: 'payment',
                label: 'Payment',
              },
              {
                value: 'completed',
                label: 'Complete',
              },
            ]}
            showArrow
          />
        </Form.Item> */}

        <Form.Item name='date'>
          <DatePicker.RangePicker disabledDate={(current) => current.isAfter(moment())} />
        </Form.Item>

        <Button icon={<SearchOutlined />} type='primary' htmlType='submit'>
          Search
        </Button>
      </Form>
      <Table
        rowKey='id'
        columns={columns}
        pagination={
          {
            total: state.swapList.totalDataCount,
            current: state.swapList.page_number,
            pageSize: state.swapList.page_size,
            showSizeChanger: true,
            // onChange: (pageNumber, pageSize) => {
            //   if (pageSize !== state.swapList.page_size) {
            //     dispatch(pageSizeChange({pageNumber, pageSize}));
            //   } else {
            //     dispatch(pageNumberChange(pageNumber));
            //   }
            // },
          } as TablePaginationConfig
        }
        loading={state.isLoading}
        size='middle'
        dataSource={state.swapList.pageData}
        scroll={{x: undefined, y: '69vh'}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(pagination, _, {order, field}: any) => {
          if (pagination.pageSize && pagination.pageSize !== state.swapList.page_size) {
            dispatch(pageSizeChange(pagination.pageSize));
          }
          if (pagination.current && pagination.current !== state.swapList.page_number) {
            dispatch(pageNumberChange(pagination.current));
          }
          if (field) {
            if (order === sortbyFilter.DESCEND) {
              dispatch(sortBy({ascending: false, sortBy: field}));
            } else {
              dispatch(sortBy({ascending: true, sortBy: field}));
            }
          }
        }}
      />
      {swapDetailModalOpen && (
        <SwapDetail isOpen={swapDetailModalOpen} setIsOpen={setSwapDetailModalOpen} />
      )}
    </div>
  );
};

export default Swap;
